/**
 * Session Row Component
 * Displays session information with EVV status and note actions
 */

import React, { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Divider,
  Paper,
  Tooltip,
  Grow,
  Collapse,
  Snackbar,
  Alert,
  Link,
  List,
  ListItem,
  ListItemText,
  capitalize,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Cancel as CancelIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  Sync as SyncIcon,
  Block as BlockIcon,
  Paid as PaidIcon,
  Event as EventIcon,
  LocalHospital as LocalHospitalIcon,
  CreditCard as CreditCardIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
  Gavel as GavelIcon,
  Pending as PendingIcon,
  Payment as PaymentIcon,
  Drafts as DraftsIcon,
} from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { format } from 'date-fns'
import { useAppSelector } from '@/store'
import { UserRole } from '@/types'
import {
  Session,
  Note,
  Patient,
  Therapist,
  SessionStatus,
  PaymentStatus,
  ClaimStatus,
} from '@/types/contracts'
import { notesApi } from '@/services/notesApi'
import api from '@/services/api'
import ClockInDialog from './ClockInDialog'
import ClockOutDialog from './ClockOutDialog'
import InvoiceDialog from './InvoiceDialog'
import ClaimDialog from './ClaimDialog'
import DiagnosticCodesDialog from './DiagnosticCodesDialog'
import { ExternalLink } from 'lucide-react'

interface SessionRowProps {
  session: Session
  patient: Patient
  therapist: Therapist
  contractId: string
  onStartEVV: (mode: 'check-in' | 'check-out') => void
  onUpdateSession?: (session: Session) => void
  onOpenNote: (note?: Note) => void
  onRaiseInvoice?: (sessionId: string, amount: number) => void
  onCompleteSession?: (sessionId: string) => void
  onViewClaim?: (sessionId: string) => void
  onGeneratePoC?: (sessionId: string) => void
  onReschedule?: () => void
  onCancel?: () => void
  onShowSnackbar?: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => void
  delay?: number
  isNextAvailableSession?: boolean
  readOnly?: boolean
  isCancelledContract?: boolean
  isCompletedContract?: boolean
}

const SessionRow: React.FC<SessionRowProps> = ({
  session,
  patient,
  therapist,
  contractId,
  onStartEVV,
  onUpdateSession,
  onOpenNote,
  onRaiseInvoice,
  onCompleteSession,
  onViewClaim,
  onGeneratePoC,
  onReschedule,
  onCancel,
  onShowSnackbar,
  delay = 0,
  isNextAvailableSession = false,
  readOnly = false,
  isCancelledContract = false,
  isCompletedContract = false,
}) => {
  const authUser = useAppSelector((state: any) => state.auth?.user)
  const userFromStorage = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('auth-user') || '{}')
    } catch {
      return {}
    }
  }, [])
  const activeUser = authUser || userFromStorage

  const isAgencySwitched = Boolean(
    activeUser?.agencyId ||
    activeUser?.role === 'agency' ||
    activeUser?.role === UserRole.AGENCY
  )

  const evv = session.evv || (session as any).evvs || {}
  const scheduledAt = session.scheduledAt || (session as any).session_date || (session as any).start_date

  const formatSafeDate = (dateValue: any, formatString: string): string => {
    if (!dateValue) return 'N/A'
    try {
      const parsedDate = new Date(dateValue)
      if (isNaN(parsedDate.getTime())) {
        return 'N/A'
      }
      return format(parsedDate, formatString)
    } catch (e) {
      console.error('Error formatting date:', e)
      return 'N/A'
    }
  }

  const therapistName =
    ((session as any).therapist_id?.firstName || (session as any).therapist_id?.lastName
      ? `${(session as any).therapist_id.firstName || ''} ${(session as any).therapist_id.lastName || ''}`.trim()
      : '') ||
    therapist?.name ||
    'N/A'

  // Check if session is checked in - check both EVV data and status
  const hasCheckedIn =
    !!evv.checkInAt ||
    !!evv.clock_in ||
    (session.status as string) === 'clocked-in' ||
    session.status === 'in-progress'
  const hasCheckedOut =
    !!evv.checkOutAt ||
    !!evv.clock_out ||
    session.status === 'clocked-out' ||
    session.status === 'completed'
  // Handle both old Note structure and new API structure
  const hasFinalizedNotes = session.notes.some(n => {
    if (typeof n === 'object' && 'status' in n) {
      const status = String(n.status).toLowerCase()
      return status === 'finalized'
    }
    return false
  })
  const hasDraftNotes = session.notes.some(n => {
    if (typeof n === 'object' && 'status' in n) {
      const status = String(n.status).toLowerCase()
      return status === 'draft'
    }
    return false
  })
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success')
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false)
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false)
  const [loadingNoteId, setLoadingNoteId] = useState<string | null>(null)
  const [isCompletingSession, setIsCompletingSession] = useState(false)
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [invoiceData, setInvoiceData] = useState<any>(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const [claimData, setClaimData] = useState<any>(null)
  const [loadingClaim, setLoadingClaim] = useState(false)
  const [diagnosticCodesDialogOpen, setDiagnosticCodesDialogOpen] =
    useState(false)
  const [commentModal, setCommentModal] = useState<{ open: boolean; comment: string; status: string }>({ open: false, comment: '', status: '' })
  const navigate = useNavigate()

  // Agency portal has a read-only view of session execution -
  // clock in/out, notes, mark-as-complete and PoC generation are
  // performed by the provider, not the agency.
  const agencyActionsDisabled = true

  // Determine if session is clocked out (checked out but waiting for payment processing)
  const isClockedOut = session.status === 'clocked-out' && hasCheckedOut

  // Determine if session is marked as complete (has payment info processed)
  const isSessionComplete =
    session.status === 'completed' &&
    session.paymentInfo?.paymentStatus !== 'pending'

  // Show "Mark as Complete" button when clocked out
  // For clocked-out status, always show it (regardless of finalized notes)
  // For other statuses, require finalized notes
  const canMarkComplete =
    session.status === 'clocked-out'
      ? !isSessionComplete
      : (isClockedOut || (hasCheckedOut && session.status !== 'completed')) &&
      session.status !== 'completed' &&
      !isSessionComplete &&
      hasFinalizedNotes

  // Get effective status for display (considers both status field and EVV data)
  const getEffectiveStatus = (): string => {
    // If checked in but not checked out, show as clocked-in
    if (hasCheckedIn && !hasCheckedOut) {
      return 'clocked-in'
    }
    // Otherwise use the session status
    return session.status as string
  }

  // Get display label for status (capitalize kebab-case)
  const getStatusLabel = (status: string): string => {
    // Convert kebab-case to Title Case (e.g., "clocked-in" -> "Clocked-In")
    return status
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-')
  }

  const getStatusColor = (status: SessionStatus | string) => {
    // Handle both "clocked-in" and "in-progress" as the same color
    const statusToCheck = status === 'clocked-in' ? 'in-progress' : status
    switch (statusToCheck) {
      case 'completed':
        return 'success'
      case 'clocked-out':
        return 'warning'
      case 'in-progress':
        return 'info'
      case 'scheduled':
        return 'default'
      case 'missed':
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const getEVVStatusIcon = () => {
    if (session.status === 'completed') {
      return (
        <Tooltip placement="top" title="Session marked as completed">
          <CheckCircleIcon sx={{ color: 'success.main' }} />
        </Tooltip>
      )
    }
    if (session.status === 'cancelled') {
      return (
        <Tooltip placement="top" title="Session cancelled">
          <CancelIcon sx={{ color: 'error.main' }} />
        </Tooltip>
      )
    }
    if (hasCheckedOut || session.status === 'clocked-out') {
      return (
        <Tooltip placement="top" title="Clock-in and Clock-out complete">
          <CheckCircleIcon sx={{ color: 'warning.main' }} />
        </Tooltip>
      )
    }
    if (hasCheckedIn) {
      return (
        <Tooltip placement="top" title="Clocked in - waiting for clock-out">
          <RadioButtonUncheckedIcon sx={{ color: 'warning.main' }} />
        </Tooltip>
      )
    }
    return (
      <Tooltip placement="top" title="EVV not started">
        <RadioButtonUncheckedIcon sx={{ color: 'grey.300' }} />
      </Tooltip>
    )
  }

  // Determine if clock-in button should be shown
  // Only show if: scheduled/rescheduled status AND not checked in AND not in progress
  const canCheckIn =
    (session.status === 'scheduled' || session.status === 'rescheduled') &&
    !hasCheckedIn &&
    !hasCheckedOut &&
    isNextAvailableSession

  // Determine if clock-out button should be shown
  // Show if: checked in (via EVV or status) AND not checked out
  const canCheckOut = hasCheckedIn && !hasCheckedOut
  // Can add note when checked in, clocked out, or has checked out (but not when completed)
  const canAddNote =
    (hasCheckedIn ||
      hasCheckedOut ||
      session.status === 'clocked-out' ||
      session.status === 'in-progress' ||
      (session.status as string) === 'clocked-in') &&
    session.status !== 'completed'
  // Can edit notes when clocked out (but not when completed)
  const canEditNotes =
    (session.status === 'clocked-out' || hasCheckedOut) && !isSessionComplete

  // Show reschedule/cancel buttons for scheduled sessions that haven't been checked in
  const canRescheduleOrCancel =
    (session.status === 'scheduled' ||
      (session.status as string).toLowerCase() === 'rescheduled') &&
    !hasCheckedIn &&
    !readOnly

  // Payment-related helpers
  const getPaymentStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case 'fully-paid':
        return 'success'
      //   case 'partially-paid':
      //     return 'info'
      case 'invoice-sent':
        return 'warning'
      //   case 'overdue':
      //     return 'error'
      case 'pending':
      default:
        return 'default'
    }
  }

  const getPaymentStatusIcon = (status: PaymentStatus) => {
    switch (status) {
      case 'fully-paid':
        return <CheckCircleOutlineIcon fontSize="small" />
      //   case 'partially-paid':
      //     return <HourglassEmptyIcon fontSize="small" />
      case 'invoice-sent':
        return <SendIcon fontSize="small" />
      //   case 'overdue':
      //     return <WarningIcon fontSize="small" />
      case 'pending':
      case 'in-progress':
        return <HourglassEmptyIcon fontSize="small" />
      default:
        return <HourglassEmptyIcon fontSize="small" />
    }
  }

  const getClaimStatusColor = (status: ClaimStatus) => {
    switch (status) {
      case 'paid':
      case 'approved':
        return 'success'
      case 'ack_received':
        return 'info'
      case 'payer_processing':
      case 'submitted':
        return 'info'
      case 'pending_ack':
      case 'pending':
        return 'warning'
      case 'denied':
      case 'rejected':
        return 'error'
      case 'not-applicable':
      default:
        return 'default'
    }
  }

  const getClaimStatusIcon = (status: ClaimStatus) => {
    switch (status) {
      case 'paid':
        return <PaidIcon fontSize="small" />
      case 'approved':
        return <CheckCircleIcon fontSize="small" />
      case 'ack_received':
        return <CheckCircleOutlineIcon fontSize="small" />
      case 'payer_processing':
        return <SyncIcon fontSize="small" />
      case 'submitted':
        return <SendIcon fontSize="small" />
      case 'pending_ack':
        return <AccessTimeIcon fontSize="small" />
      case 'denied':
        return <BlockIcon fontSize="small" />
      case 'rejected':
        return <CancelIcon fontSize="small" />
      case 'pending':
        return <HourglassEmptyIcon fontSize="small" />
      case 'not-applicable':
      default:
        return null
    }
  }

  const getClaimStatusLabel = (status: ClaimStatus) => {
    switch (status) {
      case 'pending_ack':
        return 'Pending Acknowledgment'
      case 'ack_received':
        return 'Accepted'
      case 'payer_processing':
        return 'In Process'
      case 'denied':
        return 'Denied'
      case 'paid':
        return 'Paid'
      case 'submitted':
        return 'Submitted'
      case 'pending':
        return 'Pending'
      case 'approved':
        return 'Approved'
      case 'rejected':
        return 'Rejected'
      default:
        return status.replace(/[-_]/g, ' ')
    }
  }

  const getClaimStatusTooltip = (status: ClaimStatus) => {
    switch (status) {
      case 'pending_ack':
        return 'Claim sent to Claim.MD'
      case 'ack_received':
        return 'Claim accepted by clearinghouse'
      case 'payer_processing':
        return 'Sent to payer'
      case 'denied':
        return 'Payer denied claim'
      case 'paid':
        return 'Remittance received'
      case 'approved':
        return 'Claim approved by payer'
      case 'rejected':
        return 'Claim rejected'
      case 'submitted':
        return 'Claim submitted'
      case 'pending':
        return 'Awaiting submission'
      default:
        return status.replace(/[-_]/g, ' ')
    }
  }

  const canRaiseInvoice =
    session.paymentInfo &&
    session.paymentInfo.patientOwedAmount > 0 &&
    session.paymentInfo.paymentStatus === 'pending' &&
    !session.paymentInfo.invoiceId &&
    hasCheckedOut

  // Only show payment info for completed sessions that have been marked complete
  const shouldShowPaymentInfo = isSessionComplete && session.paymentInfo

  // Helper function to check if diagnostic codes exist
  const hasDiagnosticCodes = (): boolean => {
    const icdCode = (session as any).icd_code
    const cptCode = (session as any).cpt_code

    // Check if icd_code exists and is not null/empty
    const hasIcdCode =
      icdCode !== null &&
      icdCode !== undefined &&
      (Array.isArray(icdCode) ? icdCode.length > 0 : icdCode !== '')

    // Check if cpt_code exists and is not null/empty
    const hasCptCode =
      cptCode !== null &&
      cptCode !== undefined &&
      (Array.isArray(cptCode) ? cptCode.length > 0 : cptCode !== '')

    return hasIcdCode && hasCptCode
  }

  // Helper function to complete session and show claim dialog (used by both paths)
  const completeSessionAndShowClaim = async () => {
    setIsCompletingSession(true)

    try {
      const sessionId = (session as any)._id || session.id
      const response = await api.patch(`/sessions/${sessionId}/complete`)

      // Show success message
      // setSnackbarMessage('Session marked as complete successfully!')
      // setSnackbarSeverity('success')
      // setSnackbarOpen(true)

      // Update the session locally if callback is provided (but don't refresh contract yet)
      if (onUpdateSession && response.data?.data) {
        onUpdateSession(response.data.data)
      }

      // Now fetch claim details and show dialog (without refreshing contract)
      setLoadingClaim(true)
      setClaimDialogOpen(true)

      try {
        const claimResponse = await api.get(
          `/insurance-claims/claim-detail/${sessionId}`
        )
        if (claimResponse.data) {
          // Extract MongoDB _id from sessionId object if it exists, otherwise use the MongoDB _id we have
          // sessionId variable here is already the MongoDB _id
          let mongoSessionId = sessionId
          if (
            claimResponse.data.sessionId &&
            typeof claimResponse.data.sessionId === 'object' &&
            claimResponse.data.sessionId._id
          ) {
            mongoSessionId = claimResponse.data.sessionId._id
          }
          // Ensure MongoDB _id is set in claim data for the payload
          const claimDataWithSessionId = {
            ...claimResponse.data,
            sessionIdMongoId: sessionId, // Store MongoDB _id for payload
            sessionId: claimResponse.data.sessionId, // Keep original sessionId object for display
          }
          setClaimData(claimDataWithSessionId)
        }
      } catch (err: any) {
        console.error('Error fetching claim:', err)
        setSnackbarMessage('Failed to load claim details. Please try again.')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setClaimDialogOpen(false)
      } finally {
        setLoadingClaim(false)
      }
    } catch (err: any) {
      console.error('Error completing session:', err)
      const errorMessage =
        err.response?.data?.message ||
        'Failed to complete session. Please try again.'
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setIsCompletingSession(false)
    }
  }

  const handleMarkComplete = async () => {
    // Check payment method
    const sessionPaymentMethod = session.payment_method

    if (sessionPaymentMethod === 'out_of_pocket') {
      // First, complete the session
      setIsCompletingSession(true)

      try {
        const sessionId = (session as any)._id || session.id
        const response = await api.patch(`/sessions/${sessionId}/complete`)

        // Show success message
        // setSnackbarMessage('Session marked as complete successfully!')
        // setSnackbarSeverity('success')
        // setSnackbarOpen(true)

        // Update the session locally if callback is provided (but don't refresh contract yet)
        if (onUpdateSession && response.data?.data) {
          onUpdateSession(response.data.data)
        }

        // Now fetch invoice details and show dialog (without refreshing contract)
        setLoadingInvoice(true)
        setInvoiceDialogOpen(true)

        try {
          const invoiceResponse = await api.get(
            `/invoices/invoice-detail/${sessionId}`
          )
          if (invoiceResponse.data) {
            setInvoiceData(invoiceResponse.data)
          }
        } catch (err: any) {
          console.error('Error fetching invoice:', err)
          setSnackbarMessage(
            'Failed to load invoice details. Please try again.'
          )
          setSnackbarSeverity('error')
          setSnackbarOpen(true)
          setInvoiceDialogOpen(false)
        } finally {
          setLoadingInvoice(false)
        }
      } catch (err: any) {
        console.error('Error completing session:', err)
        const errorMessage =
          err.response?.data?.message ||
          'Failed to complete session. Please try again.'
        setSnackbarMessage(errorMessage)
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      } finally {
        setIsCompletingSession(false)
      }
      return
    }

    if (sessionPaymentMethod === 'insurance') {
      // Check if diagnostic codes already exist
      if (hasDiagnosticCodes()) {
        // Codes already exist, skip diagnostic codes dialog and directly show claim dialog
        await completeSessionAndShowClaim()
      } else {
        // Codes don't exist, show diagnostic codes dialog first
        setDiagnosticCodesDialogOpen(true)
      }
      return
    }

    // For other payment methods, proceed with normal completion
    await completeSession()
  }

  const completeSession = async () => {
    setIsCompletingSession(true)

    try {
      // Use session._id if available, otherwise fall back to session.id
      const sessionId = (session as any)._id || session.id
      const response = await api.patch(`/sessions/${sessionId}/complete`)

      // Show success message
      // setSnackbarMessage('Session marked as complete successfully!')
      // setSnackbarSeverity('success')
      // setSnackbarOpen(true)

      // Call the callback if provided - this will trigger contract refresh
      if (onCompleteSession) {
        await onCompleteSession(session.id)
      }

      // Update the session if callback is provided
      if (onUpdateSession && response.data?.data) {
        onUpdateSession(response.data.data)
      }
    } catch (err: any) {
      console.error('Error completing session:', err)
      const errorMessage =
        err.response?.data?.message ||
        'Failed to complete session. Please try again.'
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setIsCompletingSession(false)
    }
  }

  const handleGenerateInvoice = async (payload: {
    session_id: string
    bill_to?: string | null
    bill_address?: string | null
    bill_city?: string | null
    bill_state?: string | null
    bill_zip?: string | null
    discount: number
    amount: number
    service_name?: string | null
  }) => {
    try {
      // Call the generate invoice API
      const response = await api.post('/invoices/generate', payload)

      if (response.data) {
        const successMessage =
          response.data.message || 'Invoice generated successfully!'

        // Use parent snackbar if available, otherwise fall back to local snackbar
        if (onShowSnackbar) {
          onShowSnackbar(successMessage, 'success')
        } else {
          setSnackbarMessage(successMessage)
          setSnackbarSeverity('success')
          setSnackbarOpen(true)
        }

        // Close the invoice dialog
        handleInvoiceDialogClose()
      }
    } catch (err: any) {
      console.error('Error generating invoice:', err)
      const errorMessage =
        err.response?.data?.message ||
        'Failed to generate invoice. Please try again.'

      // Use parent snackbar if available, otherwise fall back to local snackbar
      if (onShowSnackbar) {
        onShowSnackbar(errorMessage, 'error')
      } else {
        setSnackbarMessage(errorMessage)
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }
    }
  }

  const handleInvoiceDialogClose = () => {
    setInvoiceDialogOpen(false)
    setInvoiceData(null)
    // Refresh contract when dialog is closed (whether invoice was generated or not)
    if (onCompleteSession) {
      onCompleteSession(session.id)
    }
  }

  // Handle viewing invoice from claim co-pay
  const handleViewClaimInvoice = async (invoiceId: string) => {
    setLoadingInvoice(true)
    setInvoiceDialogOpen(true)

    try {
      // Try invoice endpoint first, fallback to invoice-detail with session_id
      let response
      try {
        response = await api.get(`/invoices/${invoiceId}`)
      } catch {
        // Fallback to invoice-detail endpoint using session_id
        const sessionId = (session as any)._id || session.id
        response = await api.get(`/invoices/invoice-detail/${sessionId}`)
      }

      if (response.data) {
        setInvoiceData(response.data)
      }
    } catch (err: any) {
      console.error('Error fetching invoice details:', err)
      setSnackbarMessage('Failed to load invoice details. Please try again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setInvoiceDialogOpen(false)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const handleGenerateClaim = async (payload: any) => {
    try {
      // Call the update-full-claim API
      const response = await api.post(
        '/insurance-claims/update-full-claim',
        payload
      )

      if (response.data) {
        const successMessage =
          response.data.message || 'Claim submitted successfully!'
        // Show success message
        if (onShowSnackbar) {
          onShowSnackbar(successMessage, 'success')
        } else {
          setSnackbarMessage(successMessage)
          setSnackbarSeverity('success')
          setSnackbarOpen(true)
        }

        // Close the claim dialog (handleClaimDialogClose will handle refresh)
        handleClaimDialogClose()
      }
    } catch (err: any) {
      console.error('Error submitting claim:', err)
      const errorMessage =
        err.response?.data?.message ||
        'Failed to submit claim. Please try again.'
      if (onShowSnackbar) {
        onShowSnackbar(errorMessage, 'error')
      } else {
        setSnackbarMessage(errorMessage)
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }
    }
  }

  const handleClaimDialogClose = () => {
    setClaimDialogOpen(false)
    // Refresh contract when dialog is closed (whether claim was submitted or not)
    if (onCompleteSession) {
      onCompleteSession(session.id)
    }
  }

  const handleDiagnosticCodesSuccess = async () => {
    // After diagnostic codes are submitted, complete the session and show claim dialog
    await completeSessionAndShowClaim()
  }

  const handleGeneratePoC = () => {
    // Check if PoC is already generated
    if (session.isPocGenerated) {
      setSnackbarMessage(
        'PoC document has already been generated for this session'
      )
      setSnackbarSeverity('info')
      setSnackbarOpen(true)
      return
    }

    // Check if session has any Initial Evaluation Report
    if (!session.notes || session.notes.length === 0) {
      setSnackbarMessage(
        'Please add Initial Evaluation Report to this session before generating PoC'
      )
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
      return
    }

    // Proceed with PoC generation if notes exist
    if (onGeneratePoC) {
      onGeneratePoC(session.id)
    }
  }

  const handleRaiseInvoiceClick = () => {
    if (onRaiseInvoice && session.paymentInfo) {
      const amount = session.paymentInfo.patientOwedAmount
      const invoiceId = `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}`

      // Show success message
      setSnackbarMessage(
        `Invoice ${invoiceId} generated successfully for $${amount.toFixed(2)}`
      )
      setSnackbarSeverity('success')
      setSnackbarOpen(true)

      // Call the callback
      onRaiseInvoice(session.id, amount)
    }
  }

  const handleNoteClick = async (note: any) => {
    // If it's the new API structure with _id, fetch the full note details
    if (note._id && !note.id) {
      setLoadingNoteId(note._id)
      try {
        // const noteDetail = await notesApi.getNoteById(note._id)
        // Convert API response to Note type, preserving noteCategory info
        // const convertedNote: Note & { noteCategory?: any } = {
        //   id: noteDetail._id,
        //   sessionId: noteDetail.sessionId,
        //   type: 'SOAP' as any, // Default type
        //   text: noteDetail.content,
        //   cptCodes: [],
        //   icdCodes: [],
        //   patientVisible: noteDetail.visibleto_patient,
        //   status:
        //     String(noteDetail.status || '').toLowerCase() === 'finalized'
        //       ? 'finalized'
        //       : 'draft',
        //   createdAt: noteDetail.createdAt,
        //   updatedAt: noteDetail.updatedAt,
        //   noteCategory: noteDetail.noteCategory || note.noteCategory,
        // }
        onOpenNote(note as any)
      } catch (err: any) {
        setSnackbarMessage(
          err.message || 'Failed to load note details. Please try again.'
        )
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      } finally {
        setLoadingNoteId(null)
      }
    } else {
      // Old structure, use as is
      onOpenNote(note)
    }
  }

  // Helper to get note title from either structure
  const getNoteTitle = (note: any): string => {
    if (note.noteCategory?.title) {
      return note.noteCategory.title
    }
    if (note.type) {
      return `${note.type} Note`
    }
    return 'Clinical Note'
  }

  // Helper to get payment method display info
  const getPaymentMethodInfo = () => {
    const paymentMethod =
      (session as any).payment_method || session.payment_method
    if (paymentMethod === 'insurance') {
      return {
        label: 'Insurance',
        icon: <LocalHospitalIcon fontSize="small" color="action" />,
        color: '#2563eb',
        bgColor: '#dbeafe',
        borderColor: '#93c5fd',
      }
    }
    if (paymentMethod === 'out_of_pocket') {
      return {
        label: 'Out of Pocket',
        icon: <CreditCardIcon fontSize="small" color="action" />,
        color: '#059669',
        bgColor: '#d1fae5',
        borderColor: '#6ee7b7',
      }
    }
    return null
  }

  // Check if payment information should be shown (for out_of_pocket)
  const shouldShowPaymentAccordion = () => {
    const paymentMethod =
      (session as any).payment_method || session.payment_method
    if (paymentMethod !== 'out_of_pocket') {
      return false
    }
    // Check if invoiceId exists and is not null
    // invoiceId should be an object with invoice details (as shown in user's example)
    const invoiceId = (session as any).invoiceId
    // Check if invoiceId is an object with at least invoice_ids field
    return (
      invoiceId !== null &&
      invoiceId !== undefined &&
      typeof invoiceId === 'object' &&
      invoiceId.invoice_ids !== undefined
    )
  }

  // Check if claim information should be shown (for insurance)
  const shouldShowClaimAccordion = () => {
    const paymentMethod =
      (session as any).payment_method || session.payment_method
    if (paymentMethod !== 'insurance') {
      return false
    }
    // Check if claimId exists and is not null
    const claimId = (session as any).claimId
    // Check if claimId is an object with at least claimId field
    return (
      claimId !== null &&
      claimId !== undefined &&
      typeof claimId === 'object' &&
      claimId.claimId !== undefined
    )
  }

  // Get invoice data from session
  const getInvoiceData = () => {
    const invoiceId = (session as any).invoiceId
    // Return the invoice object if it exists and is an object
    if (
      invoiceId !== null &&
      invoiceId !== undefined &&
      typeof invoiceId === 'object'
    ) {
      return invoiceId
    }
    return null
  }

  // Get claim data from session
  const getClaimData = () => {
    const claimId = (session as any).claimId
    // Return the claim object if it exists and is an object
    if (
      claimId !== null &&
      claimId !== undefined &&
      typeof claimId === 'object'
    ) {
      return claimId
    }
    return null
  }

  // Handle invoice download
  const handleInvoiceDownload = () => {
    const invoiceData = getInvoiceData()
    if (invoiceData?.invoice_url) {
      window.open(invoiceData.invoice_url, '_blank')
    } else {
      setSnackbarMessage('Invoice download URL not available')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
    }
  }

  // Handle claim document download
  const handleClaimDocumentDownload = () => {
    const claimData = getClaimData()
    if (claimData?.documentUrl) {
      const link = document.createElement('a')
      link.href = claimData.documentUrl
      link.download = `claim-${claimData.claimId || claimData._id}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      setSnackbarMessage('No document available for download.')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
    }
  }

  // Get invoice ID from claim (for co-pay)
  const getClaimInvoiceId = (claimData: any): string => {
    if (
      typeof claimData.invoiceId === 'object' &&
      claimData.invoiceId !== null
    ) {
      return claimData.invoiceId.invoice_ids || claimData.invoiceId._id || ''
    }
    return claimData.invoiceId || claimData.invoice_id || ''
  }

  // Get co-pay amount from claim
  const getClaimCoPayAmount = (claimData: any): number => {
    if (claimData.coPayAmount !== undefined) {
      return claimData.coPayAmount
    }
    if (
      claimData.sessionId &&
      typeof claimData.sessionId === 'object' &&
      claimData.sessionId.co_pay !== undefined
    ) {
      return claimData.sessionId.co_pay
    }
    return claimData.co_pay ?? claimData.coPay ?? 0
  }

  // Get status config for invoice status
  const getInvoiceStatusConfig = (status: string) => {
    switch (status) {
      case 'Paid':
        return {
          color: 'success',
          label: 'Paid',
          icon: <PaymentIcon fontSize="small" />,
        }
      case 'Pending':
        return {
          color: 'warning',
          label: 'Pending',
          icon: <PendingIcon fontSize="small" />,
        }
      case 'Draft':
        return {
          color: 'default',
          label: 'Draft',
          icon: <DraftsIcon fontSize="small" />,
        }
      default:
        return { color: 'default', label: status, icon: null }
    }
  }

  // Get status config for claim status
  const getClaimStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return {
          color: 'warning',
          label: 'Pending',
          icon: <PendingIcon fontSize="small" />,
        }
      case 'submitted':
        return {
          color: '#17a2b8',
          label: 'Submitted',
          icon: <SendIcon fontSize="small" />,
        }
      case 'accepted':
        return {
          color: 'info',
          label: 'Accepted',
          icon: <CheckCircleIcon fontSize="small" />,
        }
      case 'rejected':
        return {
          color: 'error',
          label: 'Rejected',
          icon: <CancelIcon fontSize="small" />,
        }
      case 'paid':
        return {
          color: 'success',
          label: 'Paid',
          icon: <PaymentIcon fontSize="small" />,
        }
      default:
        return { color: '#6c757d', label: capitalize(status), icon: null }
    }
  }

  // Format date for display
  const formatPaymentDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  return (
    <Grow
      in
      timeout={300}
      style={{ transformOrigin: '0 0 0', transitionDelay: `${delay}ms` }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          '&:hover': {
            boxShadow: 1,
            borderColor: 'primary.light',
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="start"
          flexWrap="wrap"
          gap={2}
        >
          {/* Left Section - Session Info */}
          <Box flex={1} minWidth={{ xs: '100%', sm: '250px' }} maxWidth={{ xs: '100%', md: '450px' }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
              {getEVVStatusIcon()}
              <Typography
                variant="h6"
                sx={{ fontSize: '1rem', wordBreak: 'break-word' }}
              >
                Session ID: {session.id}
              </Typography>
              <Chip
                label={getStatusLabel(getEffectiveStatus())}
                color={getStatusColor(getEffectiveStatus())}
                size="small"
              />
            </Box>

            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatSafeDate(scheduledAt, 'MMM dd, yyyy')}
                </Typography>
              </Box>

              {(session as any).session_slot && (
                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {(session as any).session_slot}
                  </Typography>
                </Box>
              )}

              {session.locationType && (
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOnIcon fontSize="small" color="action" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {session.locationType}
                  </Typography>
                </Box>
              )}

              {/* Payment Method */}
              {getPaymentMethodInfo() && (
                <Box display="flex" alignItems="center" gap={1}>
                  {getPaymentMethodInfo()?.icon}
                  <Chip
                    label={getPaymentMethodInfo()?.label}
                    size="small"
                    sx={{
                      backgroundColor: getPaymentMethodInfo()?.bgColor,
                      color: getPaymentMethodInfo()?.color,
                      border: `1px solid ${getPaymentMethodInfo()?.borderColor}`,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Box>
              )}

              {session.durationMinutes && (
                <Typography variant="caption" color="text.secondary" mb={0.5}>
                  Slot Duration: {session.slotDurationMinutes} minutes
                </Typography>
              )}

                <Box display="flex" alignItems="center" gap={1} mb={1} mt={0.5}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Assign to:
                  </Typography>
                  <Chip
                    label={therapistName}
                    size="small"
                    sx={{
                      backgroundColor: 'aliceblue',
                      color: '#2E7DAF',
                      border: '1px solid #B0D4ED',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 22,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Box>
            

              {/* Generate PoC Button - Show for all sessions */}
              {onGeneratePoC &&
                !readOnly &&
                !agencyActionsDisabled &&
                !isCompletedContract &&
                // session.status !== 'completed' &&
                session.status !== 'cancelled' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    onClick={handleGeneratePoC}
                    disabled={session.isPocGenerated === true}
                    sx={{
                      mt: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      borderColor: session.isPocGenerated
                        ? 'grey.300'
                        : '#2E7DAF',
                      color: session.isPocGenerated ? 'grey.500' : '#2E7DAF',
                      '&:hover': {
                        borderColor: session.isPocGenerated
                          ? 'grey.300'
                          : '#2563a0',
                        backgroundColor: session.isPocGenerated
                          ? 'transparent'
                          : 'rgba(46, 125, 175, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'grey.300',
                        color: 'grey.500',
                      },
                    }}
                  >
                    {session.isPocGenerated ? 'PoC Generated' : 'Generate PoC'}
                  </Button>
                )}
            </Box>
          </Box>

          {/* Middle Section - Clock-In/Out Actions */}
          <Box
            minWidth={{ xs: '100%', sm: '250px' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            gap={1.5}
            flex={1}
            maxWidth={{ xs: '100%', md: '350px' }}
          >
            {/* Clock-In/Out Buttons */}
            {(canCheckIn || canCheckOut) && !readOnly && !agencyActionsDisabled && (
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                justifyContent="center"
                alignItems="center"
              >
                {canCheckIn && (
                  <>
                    <Button
                      size="medium"
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => setClockInDialogOpen(true)}
                      sx={{
                        minWidth: 140,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      Clock In
                    </Button>
                    <ClockInDialog
                      open={clockInDialogOpen}
                      onClose={() => setClockInDialogOpen(false)}
                      session={session}
                      patient={patient}
                      therapist={therapist}
                      contractId={contractId}
                      onSuccess={updatedSession => {
                        setClockInDialogOpen(false)
                        // Optimistically update the session immediately
                        if (onUpdateSession) {
                          onUpdateSession(updatedSession)
                        }
                        // Refresh after a delay to sync with server, but optimistic update will be preserved
                        onStartEVV('check-in')
                      }}
                    />
                  </>
                )}
                {canCheckOut && (
                  <>
                    <Button
                      size="medium"
                      variant="contained"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={() => setClockOutDialogOpen(true)}
                      sx={{
                        minWidth: 140,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      Clock Out
                    </Button>
                    <ClockOutDialog
                      open={clockOutDialogOpen}
                      onClose={() => setClockOutDialogOpen(false)}
                      session={session}
                      patient={patient}
                      therapist={therapist}
                      contractId={contractId}
                      onSuccess={updatedSession => {
                        setClockOutDialogOpen(false)
                        // Optimistically update the session immediately
                        if (onUpdateSession) {
                          onUpdateSession(updatedSession)
                        }
                        // Also trigger refresh to sync with server
                        onStartEVV('check-out')
                      }}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Read-only indicator for pending/cancelled contracts */}
            {readOnly && (canCheckIn || canCheckOut) && (
              <Box textAlign="center">
                <Chip
                  label={
                    isCancelledContract ? 'Contract Terminated' : 'Preview Only'
                  }
                  size="small"
                  color={isCancelledContract ? 'error' : 'default'}
                  variant="outlined"
                  sx={{ fontStyle: 'italic' }}
                />
                <Typography
                  variant="caption"
                  color={isCancelledContract ? 'error.main' : 'text.secondary'}
                  display="block"
                  mt={0.5}
                >
                  {isCancelledContract
                    ? 'Contract is cancelled. Actions are disabled.'
                    : 'Sign contract to enable'}
                </Typography>
              </Box>
            )}

            {/* Active Session Status */}
            {((hasCheckedIn && !hasCheckedOut) ||
              (session.status as string) === 'clocked-in' ||
              session.status === 'in-progress') && (
                <Box
                  sx={{
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 3,
                    p: 2,
                    textAlign: 'center',
                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)',
                      },
                      '50%': {
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.6)',
                      },
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4ade80',
                        animation: 'blink 1.5s infinite',
                        '@keyframes blink': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.3 },
                        },
                      }}
                    />
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ letterSpacing: '0.5px' }}
                    >
                      ACTIVE SESSION
                    </Typography>
                  </Box>
                </Box>
              )}

            {/* Completed Session Status */}
            {(hasCheckedIn && hasCheckedOut) ||
              session.status === 'clocked-out' ||
              session.status === 'completed' ? (
              <Box
                sx={{
                  background: isSessionComplete
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  borderRadius: 3,
                  // p: 2,
                  textAlign: 'center',
                  boxShadow: isSessionComplete
                    ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                    : '0 4px 12px rgba(245, 158, 11, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  width: '100%',
                  maxWidth: 280,
                  margin: '0 auto',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={0.5}
                >
                  {isSessionComplete ? (
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <HourglassEmptyIcon sx={{ fontSize: 16 }} />
                  )}
                  <Typography
                  // variant="body2"
                  // fontWeight={500}
                  // sx={{ letterSpacing: '0.5px' }}
                  >
                    {isSessionComplete ? 'SESSION COMPLETED' : 'CLOCKED OUT'}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {/* Scheduled Session - Awaiting Start */}
            {!hasCheckedIn &&
              !canCheckIn &&
              session.status !== 'cancelled' &&
              session.status !== 'completed' &&
              session.status !== 'clocked-out' && (
                <Box
                  sx={{
                    background:
                      'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                    color: '#6b7280',
                    borderRadius: 3,
                    p: 2,
                    textAlign: 'center',
                    border: '2px dashed #d1d5db',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    display="block"
                    mb={0.5}
                  >
                    SESSION SCHEDULED
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatSafeDate(scheduledAt, 'h:mm a')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    Awaiting Clock-In
                  </Typography>
                </Box>
              )}

            {/* Cancelled Session */}
            {session.status === 'cancelled' && (
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  color: '#991b1b',
                  borderRadius: 3,
                  p: 2,
                  textAlign: 'center',
                  border: '2px solid #fca5a5',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={0.5}
                  mb={0.5}
                >
                  <CancelIcon sx={{ fontSize: 16 }} />
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ letterSpacing: '0.5px' }}
                  >
                    SESSION CANCELLED
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  This session has been cancelled
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {formatSafeDate(scheduledAt, 'MMM dd, yyyy h:mm a')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Section - EVV Status */}
          <Box minWidth={{ xs: '100%', sm: '180px' }} flex={{ xs: 1, md: 'none' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              EVV Status
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5}>
              {(hasCheckedIn ||
                session.status === 'clocked-out' ||
                session.status === 'completed') && (
                  <Typography variant="body2" color="success.main">
                    ✓ Clock-in:{' '}
                    {evv.checkInAt
                      ? formatSafeDate(evv.checkInAt, 'h:mm a')
                      : evv.clock_in
                        ? formatSafeDate(evv.clock_in, 'h:mm a')
                        : 'N/A'}
                  </Typography>
                )}
              {(hasCheckedOut ||
                session.status === 'clocked-out' ||
                session.status === 'completed') && (
                  <Typography variant="body2" color="success.main">
                    ✓ Clock-out:{' '}
                    {evv.checkOutAt
                      ? formatSafeDate(evv.checkOutAt, 'h:mm a')
                      : evv.clock_out
                        ? formatSafeDate(evv.clock_out, 'h:mm a')
                        : 'N/A'}
                  </Typography>
                )}
              {!hasCheckedIn &&
                session.status !== 'clocked-out' &&
                session.status !== 'completed' && (
                  <Typography variant="body2" color="text.secondary">
                    Not started
                  </Typography>
                )}
              {evv.manualOverride && (
                <Tooltip placement="top" title="Manual location override applied">
                  <Chip
                    icon={<WarningIcon />}
                    label="Manual Override"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Right Section - Notes & Actions */}
          <Box width="220px">
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Clinical Notes ({session.notes.length})
            </Typography>

            {/* Notes List */}
            <Box display="flex" flexDirection="column" gap={0.5} mb={1}>
              {session.notes.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No notes yet
                </Typography>
              )}
              {session.notes.map((note: any) => {
                const noteId = note._id || note.id
                const noteStatus =
                  note.status?.toLowerCase() === 'finalized'
                    ? 'finalized'
                    : note.status?.toLowerCase() === 'draft'
                      ? 'draft'
                      : note.status
                const isDraft = noteStatus === 'draft' || noteStatus === 'Draft'
                const isReadOnly =
                  readOnly || session.status === 'completed' || !canEditNotes

                const rawStatus = (note.status || '').toString().toLowerCase()
                const isApprovedNote = note.isApprovedAgency === true || rawStatus === 'approved'
                const isRevisedNote = rawStatus === 'revised' || rawStatus === 'revision'
                const isRejectNote = rawStatus === 'reject' || rawStatus === 'rejected'
                const noteComment = note.comment || ''

                return (
                  <Box
                    key={noteId}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      p: 0,
                      borderRadius: 1,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        transition: 'all 0.2s ease-in-out',
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleNoteClick(note)}
                  >
                    {loadingNoteId === noteId ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DescriptionIcon fontSize="small" color="action" />
                    )}
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getNoteTitle(note)}
                      </Typography>
                    </Box>
                    {isDraft && (
                      <Chip
                        label="Draft"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          '& .MuiChip-label': { px: 1 },
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setCommentModal({ open: true, comment: noteComment, status: 'Draft' })
                        }}
                      />
                    )}
                    {isAgencySwitched && (isApprovedNote || isRevisedNote || isRejectNote) && (() => {
                      let label = ''
                      let color: 'success' | 'warning' | 'error' | 'default' = 'default'

                      if (isApprovedNote) {
                        label = 'Approved'
                        color = 'success'
                      } else if (isRevisedNote) {
                        label = 'Revised'
                        color = 'warning'
                      } else if (isRejectNote) {
                        label = 'Reject'
                        color = 'error'
                      }

                      return (
                        <Chip
                          label={label}
                          size="small"
                          color={color}
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 },
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setCommentModal({ open: true, comment: noteComment, status: label })
                          }}
                        />
                      )
                    })()}
                  </Box>
                )
              })}
            </Box>

            {/* Add Note Button */}
            <Box display="flex" gap={1} flexWrap="wrap" flexDirection="column">

              {!agencyActionsDisabled && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => onOpenNote()} // Open new note
                  fullWidth
                >
                  Add Note
                </Button>
              )}

              {canAddNote && readOnly && (
                <Tooltip placement="top" title={
                  isCancelledContract
                    ? 'Contract is cancelled. Note creation is disabled.'
                    : 'Sign contract to enable note creation'
                }
                >
                  <span style={{ width: '100%' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      fullWidth
                      disabled
                    >
                      Add Note
                    </Button>
                  </span>
                </Tooltip>
              )}

              {/* Reschedule and Cancel Buttons */}
              {canRescheduleOrCancel && (onReschedule || onCancel) && (
                <Box display="flex" gap={1} flexDirection="column">
                  {onReschedule && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SyncIcon />}
                      onClick={onReschedule}
                      fullWidth
                      sx={{
                        backgroundColor: '#796FA1',
                        '&:hover': {
                          backgroundColor: '#796FA1',
                        },
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Reschedule
                    </Button>
                  )}
                  {onCancel && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={onCancel}
                      fullWidth
                      sx={{
                        borderColor: 'error.main',
                        color: 'error.main',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'error.dark',
                          backgroundColor: 'error.lighter',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              )}

              {/* Mark as Complete Button */}
              {canMarkComplete && !readOnly && !agencyActionsDisabled && onCompleteSession && session.notes.length > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={
                    isCompletingSession ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ReceiptIcon />
                    )
                  }
                  onClick={handleMarkComplete}
                  disabled={isCompletingSession}
                  fullWidth
                  sx={{
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                    },
                  }}
                >
                  {isCompletingSession ? 'Completing...' : 'Mark as Complete'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Payment Information - Inline Display for Out of Pocket Sessions with Invoice */}
        {shouldShowPaymentAccordion() &&
          (() => {
            const invoiceData = getInvoiceData()
            if (!invoiceData) return null

            const statusConfig = getInvoiceStatusConfig(
              invoiceData.status || 'Pending'
            )

            return (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 3,
                  flexWrap: 'wrap',
                }}
              >
                {/* Invoice ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Invoice ID:
                  </Typography>
                  <Link
                    onClick={() =>
                      navigate('/agency-invoices', {
                        state: { searchQuery: invoiceData.invoice_ids || '' },
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#2E7DAF',
                      textDecoration: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#2563a0',
                      },
                    }}
                  >
                    {invoiceData.invoice_ids || 'N/A'}
                    <ExternalLink
                      className="w-3 h-3"
                      style={{ color: 'primary.main' }}
                    />
                  </Link>
                </Box>

                {/* Payment Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Payment Date:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatPaymentDate(invoiceData.payment_date)}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Status:
                  </Typography>
                  <Chip
                    label={statusConfig.label}
                    icon={statusConfig.icon}
                    color={statusConfig.color as any}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>
              </Box>
            )
          })()}

        {/* Claim Information - Inline Display for Insurance Sessions with Claim */}
        {shouldShowClaimAccordion() &&
          (() => {
            const claimData = getClaimData()
            if (!claimData) return null

            const statusConfig = getClaimStatusConfig(
              claimData.status || 'pending'
            )

            return (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 3,
                  flexWrap: 'wrap',
                }}
              >
                {/* Claim ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Claim ID:
                  </Typography>
                  <Link
                    onClick={() =>
                      navigate('/agency-claims', {
                        state: {
                          searchQuery: claimData.claimId || claimData._id || '',
                        },
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#2E7DAF',
                      textDecoration: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#2563a0',
                      },
                    }}
                  >
                    {claimData.claimId || claimData._id || 'N/A'}
                    <ExternalLink
                      className="w-3 h-3"
                      style={{ color: 'primary.main' }}
                    />
                  </Link>
                </Box>

                {/* Submission Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Submission Date:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatPaymentDate(
                      claimData.submissionDate || claimData.submission_date
                    )}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Status:
                  </Typography>
                  <Chip
                    icon={statusConfig.icon}
                    label={statusConfig.label}
                    color={statusConfig.color as any}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>
              </Box>
            )
          })()}

        {/* Invoice Dialog */}
        <InvoiceDialog
          open={invoiceDialogOpen}
          onClose={handleInvoiceDialogClose}
          invoiceData={invoiceData}
          loading={loadingInvoice}
          readOnly={!!invoiceData && !onRaiseInvoice}
          onGenerateInvoice={handleGenerateInvoice}
        />

        {/* Diagnostic Codes Dialog */}
        <DiagnosticCodesDialog
          open={diagnosticCodesDialogOpen}
          onClose={() => setDiagnosticCodesDialogOpen(false)}
          session={session}
          contractId={contractId}
          patient={patient}
          onSuccess={handleDiagnosticCodesSuccess}
        />

        {/* Claim Dialog */}
        <ClaimDialog
          open={claimDialogOpen}
          onClose={handleClaimDialogClose}
          claimData={claimData}
          loading={loadingClaim}
          showStatusChip={false}
          onGenerateClaim={handleGenerateClaim}
        />

        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Agency Comment Modal */}
        <Dialog
          open={commentModal.open}
          onClose={() => setCommentModal({ open: false, comment: '', status: '' })}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={commentModal.status}
                size="small"
                color={
                  commentModal.status === 'Approved' ? 'success'
                  : commentModal.status === 'Revised' || commentModal.status === 'Draft' ? 'warning'
                  : 'error'
                }
                variant="outlined"
              />
              <Typography variant="subtitle1" fontWeight={600}>
                Note Comment
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {commentModal.comment && commentModal.comment.trim() ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {commentModal.comment}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No comment provided.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setCommentModal({ open: false, comment: '', status: '' })}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Grow>
  )
}

export default SessionRow
